/**
 * PropertyAutocomplete
 *
 * Wraps the "Property name" input on the landing form with a Google Places
 * autocomplete dropdown. When the user picks a suggestion we fetch full
 * Place Details from our backend proxy and hand back a normalized object
 * to the parent: { name, city, state, country, website, place_id }.
 *
 * The parent decides what to do with the fields — this component only knows
 * about the property-name text input and the dropdown of suggestions.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, MapPin } from "lucide-react";
import { placesAutocomplete, placesDetails } from "@/lib/api";

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

export default function PropertyAutocomplete({
  value,
  onChange,
  onPick,
  placeholder = "e.g. Nassau Inn",
  testId,
  required = true,
  autoFocus = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pickingId, setPickingId] = useState(null); // place_id currently being resolved
  const cancelRef = useRef(null); // axios abort controller
  const suppressNextFetch = useRef(false); // don't re-fetch right after picking
  const containerRef = useRef(null);

  // Debounced fetch on `value` changes.
  useEffect(() => {
    if (suppressNextFetch.current) {
      suppressNextFetch.current = false;
      return;
    }
    if (!value || value.trim().length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      // Cancel any in-flight request
      if (cancelRef.current) cancelRef.current.abort();
      const controller = new AbortController();
      cancelRef.current = controller;
      setBusy(true);
      try {
        const res = await placesAutocomplete(value.trim(), { signal: controller.signal });
        setSuggestions(res.suggestions || []);
        setOpen((res.suggestions || []).length > 0);
        setActiveIndex(-1);
      } catch (err) {
        if (!axios.isCancel(err) && err?.name !== "CanceledError") {
          // Silent — autocomplete is a nice-to-have; typing still works.
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);

  // Click outside → close dropdown.
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handlePick = async (suggestion) => {
    if (!suggestion?.place_id) return;
    setPickingId(suggestion.place_id);
    setOpen(false);
    try {
      const details = await placesDetails(suggestion.place_id);
      // Set the input to the Google-canonical name and skip the next debounce fetch.
      suppressNextFetch.current = true;
      onChange({ target: { value: details.name || suggestion.main_text || value } });
      onPick?.({
        place_id: details.place_id,
        name: details.name || suggestion.main_text,
        city: details.city || "",
        state: details.state || "",
        country: details.country || "United States",
        website: details.website || "",
        formatted_address: details.formatted_address || suggestion.description,
      });
    } catch (_e) {
      // On failure, just close — user can still type manually.
    } finally {
      setPickingId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handlePick(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        className="input-base pr-10"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {(busy || pickingId) && (
        <div
          className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "var(--text-muted)" }}
        >
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}

      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-30 mt-2 left-0 right-0 rounded-xl overflow-hidden max-h-80 overflow-y-auto"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-default)",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.35)",
          }}
          data-testid="property-autocomplete-list"
          role="listbox"
        >
          {suggestions.map((s, i) => {
            const isActive = i === activeIndex;
            return (
              <li
                key={s.place_id}
                role="option"
                aria-selected={isActive}
                className="cursor-pointer px-4 py-2.5 flex items-start gap-3 border-b transition-colors"
                style={{
                  background: isActive ? "var(--bg-card-warm)" : "transparent",
                  borderColor: "var(--border-default)",
                }}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  // onMouseDown so we fire before the input's blur.
                  e.preventDefault();
                  handlePick(s);
                }}
                data-testid={`property-autocomplete-option-${i}`}
              >
                <MapPin
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: "var(--brand-sage)" }}
                />
                <div className="min-w-0 flex-1">
                  <div
                    className="text-sm font-medium truncate"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {s.main_text}
                  </div>
                  <div
                    className="text-xs truncate"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {s.secondary_text}
                  </div>
                </div>
              </li>
            );
          })}
          <li
            className="px-4 py-1.5 text-[10px] tracking-wide uppercase"
            style={{ color: "var(--text-muted)", background: "var(--bg-card-warm)" }}
          >
            Powered by Google
          </li>
        </ul>
      )}
    </div>
  );
}
